/**
 * Capacity Detail Page
 * Feature: 030-capacity-metrics (T069)
 * Constitution Version: 1.11.0
 * User Story: US4 - Workspace Capacity Attribution
 *
 * Detailed view of a single capacity showing:
 * - Summary statistics and status
 * - Hourly and daily trend charts
 * - Workspace attribution breakdown with CSV export
 * - Alert threshold configuration
 *
 * Conformed to Spec 197 — migrated off MUI (Box/Paper/Card/Chip/Tabs/Table) onto
 * the canonical Tailwind+token UI kit. Presentation-only: data/logic/state/routing
 * and the CSV export / sparkline behavior are unchanged. Charts (TrendSparkline) and
 * the WorkspaceAttributionTable child stay as-is, wrapped in <Card>.
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  XCircle,
  CheckCircle,
} from 'lucide-react';
import { useCapacityDetail } from '../../hooks/useCapacityMetrics';
import { WorkspaceAttributionTable, TrendSparkline } from '../../components/capacity';
import {
  CapacityStatus,
  TrendDirection,
  SparklineDataPoint,
  CapacityMetricDaily,
  LatestHourThrottling,
} from '@shared/types/capacityMetrics';
import { capacityMetricsApi } from '../../services/capacityMetricsApi';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Badge,
  StatCard,
  ErrorState,
  LoadingState,
  AppTooltip,
  Extended,
  QuickGlance,
  Tabs,
} from '../../components/ui';
import type { DataTableColumn, TabItem } from '../../components/ui';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';
import { unmeasuredSizeNote } from '@/utils/unmeasuredSize';
import { retiredLabel } from '@/utils/retiredLabel';

function formatManagedData(gb: number | null | undefined): string {
  if (gb == null) return '—';
  if (gb >= 1024) return `${(gb / 1024).toFixed(2)} TB`;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  return `${(gb * 1024).toFixed(0)} MB`;
}

// Throttling Events tile: telemetry windows over Fabric's throttling threshold (a percentage
// above 100) in the capacity's latest telemetry hour. A dash when nothing was measured.
function throttlingMeasured(t: LatestHourThrottling | null | undefined): t is LatestHourThrottling & { throttled_windows: number; peak_pct: number } {
  return t != null && t.throttled_windows != null && t.peak_pct != null;
}

function throttlingTileValue(t: LatestHourThrottling | null | undefined): string | number {
  return throttlingMeasured(t) ? t.throttled_windows : '—';
}

function throttlingTileSubtext(t: LatestHourThrottling | null | undefined): string {
  if (!t) return 'No throttling telemetry';
  if (!throttlingMeasured(t)) return 'No throttling percentages in the latest hour';
  return `of ${t.windows} windows over the limit in the latest hour · peak ${Math.round(t.peak_pct)}% · ${formatEastern(t.hour_start)}`;
}

// Status configuration for display (token-driven; kit <Badge>).
const statusConfig: Record<
  CapacityStatus,
  { variant: 'success' | 'warning' | 'error' | 'secondary'; icon: React.ReactNode; label: string }
> = {
  normal: { variant: 'success', icon: <CheckCircle className="h-3.5 w-3.5" />, label: 'Normal' },
  warning: { variant: 'warning', icon: <AlertTriangle className="h-3.5 w-3.5" />, label: 'Warning' },
  critical: { variant: 'error', icon: <XCircle className="h-3.5 w-3.5" />, label: 'Critical' },
  paused: { variant: 'secondary', icon: null, label: 'Paused' },
  unknown: { variant: 'secondary', icon: null, label: 'Unknown' },
};

/** A CU figure, or "no data" when nothing measured it (Spec 286 T160 item 5; the CuCapacityPanel treatment). */
const NoData: React.FC = () => <span className="text-[var(--color-text-secondary)]">no data</span>;

// Trend icon component (token colors).
const TrendIcon: React.FC<{ trend: TrendDirection }> = ({ trend }) => {
  switch (trend) {
    case 'increasing':
      return <TrendingUp className="h-4 w-4 text-[var(--color-status-danger)]" />;
    case 'decreasing':
      return <TrendingDown className="h-4 w-4 text-[var(--color-status-success)]" />;
    default:
      return <Minus className="h-4 w-4 text-[var(--color-text-secondary)]" />;
  }
};

// Capacity type display names
const capacityTypeLabels: Record<string, string> = {
  Reference: 'Type 2 - Reference',
  ClientDedicated: 'Type 3 - Client Dedicated',
  MultiTenant: 'Type 4 - Multi-Tenant',
  Platform: 'Type 1 - Platform',
};

/**
 * Main Capacity Detail Page Component
 */
export const CapacityDetailPage: React.FC = () => {
  const { capacityId } = useParams<{ capacityId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trends');
  const [isExporting, setIsExporting] = useState(false);

  const {
    data: detail,
    isLoading,
    isError,
    error,
    refetch,
  } = useCapacityDetail(capacityId || '', 168); // 7 days of history

  // Handle CSV export (T071)
  const handleExportCsv = async () => {
    if (!capacityId) return;

    setIsExporting(true);
    try {
      const csv = await capacityMetricsApi.exportAttributionCsv(capacityId);

      // Create and download file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `capacity-attribution-${capacityId}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Convert hourly history to sparkline data: measured hours only, so an hour nothing measured is a gap, not a
  // drop to 0 (Spec 286 T160 item 5).
  const hourlySparklineData: SparklineDataPoint[] = React.useMemo(() => {
    if (!detail?.hourly_history) return [];
    return detail.hourly_history.flatMap(h =>
      h.cu_used_percent === null ? [] : [{ timestamp: new Date(h.snapshot_time), value: h.cu_used_percent }]);
  }, [detail?.hourly_history]);

  // Convert daily history to sparkline data (measured days only)
  const dailySparklineData: SparklineDataPoint[] = React.useMemo(() => {
    if (!detail?.daily_history) return [];
    return detail.daily_history.flatMap(d =>
      d.avg_cu_used_percent === null ? [] : [{ timestamp: new Date(d.metric_date), value: d.avg_cu_used_percent }]);
  }, [detail?.daily_history]);

  if (isLoading) {
    return (
      <PageLayout
      helpId="auto.capacity-detail" title="Capacity Detail" description="Loading capacity details…">
        <Card>
          <LoadingState rows={6} />
        </Card>
      </PageLayout>
    );
  }

  if (isError) {
    return (
      <PageLayout
        title="Capacity Detail"
        description="Detailed capacity metrics"
        actions={
          <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/capacity-metrics')}>
            Back to Dashboard
          </Button>
        }
      >
        <ErrorState
          title="Failed to load capacity details"
          message={error instanceof Error ? error.message : undefined}
          onRetry={() => refetch()}
        />
      </PageLayout>
    );
  }

  if (!detail) {
    return (
      <PageLayout
        title="Capacity Detail"
        description="Detailed capacity metrics"
        actions={
          <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/capacity-metrics')}>
            Back to Dashboard
          </Button>
        }
      >
        <ErrorState title="Capacity not found" message="The requested capacity could not be loaded." />
      </PageLayout>
    );
  }

  const status: CapacityStatus =
    detail.current_utilization === null ? 'unknown' :
    detail.current_utilization >= 95 ? 'critical' :
    detail.current_utilization >= 80 ? 'warning' : 'normal';

  // Daily aggregates table columns (server-side history is fully loaded; DataTable
  // paginates client-side, replacing the old usePagination/DualScrollTable).
  const dailyColumns: DataTableColumn<CapacityMetricDaily>[] = [
    {
      key: 'metric_date',
      header: 'Date',
      width: '10%',
      render: (d) => formatEasternDate(d.metric_date),
    },
    // A day nothing measured holds NULL CU (migration 398): "—", like Avg Workspaces below.
    { key: 'avg_cu_used_percent', header: 'Avg CU %', width: '7%', align: 'right', render: (d) => d.avg_cu_used_percent === null ? '—' : `${d.avg_cu_used_percent.toFixed(1)}%` },
    { key: 'max_cu_used_percent', header: 'Max CU %', width: '7%', align: 'right', render: (d) => d.max_cu_used_percent === null ? '—' : `${d.max_cu_used_percent.toFixed(1)}%` },
    { key: 'min_cu_used_percent', header: 'Min CU %', width: '7%', align: 'right', render: (d) => d.min_cu_used_percent === null ? '—' : `${d.min_cu_used_percent.toFixed(1)}%` },
    { key: 'avg_storage_gb', header: 'Avg Storage (GB)', width: '9%', align: 'right', render: (d) => d.avg_storage_gb.toFixed(1) },
    { key: 'max_storage_gb', header: 'Max Storage (GB)', width: '9%', align: 'right', render: (d) => d.max_storage_gb.toFixed(1) },
    // 🛑 These three columns can now legitimately be NULL — "we had no input" rather than a
    //    fabricated 0 (migrations 276/278). A bare `.toFixed(1)` on a NULL renders "NaN", so
    //    absence is rendered as an em dash. Absence must LOOK like absence: a 0 here is a
    //    measurement claim the platform did not make.
    { key: 'total_throttling_events', header: 'Throttling', width: '8%', align: 'right', render: (d) => d.total_throttling_events ?? '—' },
    { key: 'avg_active_workspaces', header: 'Avg Workspaces', width: '9%', align: 'right', render: (d) => d.avg_active_workspaces == null ? '—' : d.avg_active_workspaces.toFixed(1) },
    { key: 'max_active_workspaces', header: 'Max Workspaces', width: '9%', align: 'right', render: (d) => d.max_active_workspaces ?? '—' },
    {
      key: 'trend_direction',
      header: 'Trend',
      width: '9%',
      render: (d) => (
        <div className="flex items-center gap-1">
          <TrendIcon trend={d.trend_direction} />
          {d.trend_percent_change != null && (
            <span className="text-[var(--color-text-secondary)]">
              {d.trend_percent_change > 0 ? '+' : ''}{d.trend_percent_change.toFixed(1)}%
            </span>
          )}
        </div>
      ),
    },
    { key: 'hourly_samples_count', header: 'Samples', width: '7%', align: 'right', render: (d) => d.hourly_samples_count },
    {
      key: 'aggregated_at',
      header: 'Aggregated',
      width: '9%',
      render: (d) => (
        <span className="text-[var(--color-text-secondary)]">{formatEastern(d.aggregated_at)}</span>
      ),
    },
  ];

  const tabs: TabItem[] = [
    { key: 'trends', label: 'Utilization Trends' },
    { key: 'attribution', label: `Workspace Attribution (${detail.workspace_attribution?.length || 0})` },
  ];

  return (
    <PageLayout
      title={
        <span className="flex items-center gap-3">
          {detail.capacity_name}
          <Badge variant="secondary" isOutlined size="sm">
            {capacityTypeLabels[detail.capacity_type] || detail.capacity_type}
          </Badge>
          <Badge variant={statusConfig[status].variant} leftIcon={statusConfig[status].icon} size="sm">
            {statusConfig[status].label}
          </Badge>
          {/* Spec 297 T032: the lists no longer carry a retired capacity; its page, opened by id, says so. */}
          {retiredLabel(detail) && (
            <Badge variant="default" size="sm">{retiredLabel(detail)}</Badge>
          )}
        </span>
      }
      description={
        <>
          <button
            type="button"
            onClick={() => navigate('/capacity-metrics')}
            className="text-[var(--color-accent)] hover:underline"
          >
            Capacity Metrics
          </button>
          {detail.region ? <span className="text-[var(--color-text-secondary)]"> · Region: {detail.region}</span> : null}
        </>
      }
      actions={
        <>
          <Button variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={() => navigate('/capacity-metrics')}>
            Back
          </Button>
          <AppTooltip title="Refresh">
            <Button variant="ghost" onClick={() => refetch()} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </AppTooltip>
        </>
      }
    >
      {/* Summary tiles */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Current Utilization"
          value={
            detail.current_utilization === null ? <NoData /> : (
              <span className="flex items-center gap-1">
                {detail.current_utilization.toFixed(1)}%
                <TrendIcon trend={detail.trend} />
              </span>
            )
          }
          subtext={
            detail.current_utilization === null ? 'Nothing measured this capacity' :
            detail.trend === 'increasing' ? 'Trending up' :
            detail.trend === 'decreasing' ? 'Trending down' : 'Stable'
          }
        />
        <StatCard label="CU Allocation" value={detail.cu_allocation ?? '—'} subtext="Compute Units" />
        <StatCard label="Active Workspaces" value={detail.workspace_attribution?.length || 0} subtext="On this capacity" />
        <StatCard
          label="Threshold Config"
          value={
            <span className="text-xl">
              ⚠️ {detail.threshold_config?.warning_percent || 80}% / 🚨 {detail.threshold_config?.critical_percent || 95}%
            </span>
          }
          subtext="Warning / Critical"
        />
        <StatCard
          label="Throttling Events"
          value={throttlingTileValue(detail.latest_hour_throttling)}
          subtext={throttlingTileSubtext(detail.latest_hour_throttling)}
        />
        {/* Spec 286 T159 item 32: the total keeps sizes the last collection did not measure; say how many. */}
        <StatCard
          label="Managed Data"
          value={formatManagedData(detail.total_storage_gb)}
          subtext={unmeasuredSizeNote(detail.sizeNotMeasuredCount) ?? 'Lakehouses + Warehouses'}
        />
      </div>

      {/* Tabs (kit Tabs: role="tablist"/"tab", as the MUI Tabs it replaced had) */}
      <Tabs tabs={tabs} value={activeTab} onChange={setActiveTab} className="mb-4" />

      {/* Tab Content */}
      {activeTab === 'trends' && (
        <div className="flex flex-col gap-6">
          {/* Hourly Trend (7 days) */}
          <Card>
            <h2 className="mb-4 text-base font-semibold text-[var(--color-text-primary)]">
              Hourly Utilization (7 Days)
            </h2>
            {hourlySparklineData.length > 0 ? (
              <TrendSparkline data={hourlySparklineData} width={800} height={200} showThresholds={true} />
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">No hourly data available</p>
            )}
          </Card>

          {/* Daily Trend (30 days) */}
          <Card>
            <h2 className="mb-4 text-base font-semibold text-[var(--color-text-primary)]">
              Daily Average Utilization (30 Days)
            </h2>
            {dailySparklineData.length > 0 ? (
              <TrendSparkline data={dailySparklineData} width={800} height={200} showThresholds={true} />
            ) : (
              <p className="text-sm text-[var(--color-text-secondary)]">No daily data available</p>
            )}
          </Card>

          {/* Daily Aggregates detail (surfaces previously-unused daily_history columns) */}
          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Open on a larger screen for the full daily-aggregates table.
            </Card>
          </QuickGlance>
          <Extended>
            <Card padding="none">
              <h2 className="px-4 pt-4 text-base font-semibold text-[var(--color-text-primary)]">
                Daily Aggregates
              </h2>
              <DataTable
                columns={dailyColumns}
                rows={detail.daily_history ?? []}
                rowKey={(d) => d.id}
                minWidth="64rem"
                emptyMessage="No daily aggregate data available"
              />
            </Card>
          </Extended>
        </div>
      )}

      {activeTab === 'attribution' && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-text-primary)]">
              Workspace Storage Attribution
            </h2>
            <Button
              variant="outline"
              leftIcon={<Download className="h-4 w-4" />}
              isLoading={isExporting}
              onClick={handleExportCsv}
              disabled={isExporting || !detail.workspace_attribution?.length}
            >
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>

          {detail.workspace_attribution && detail.workspace_attribution.length > 0 ? (
            <WorkspaceAttributionTable
              data={detail.workspace_attribution}
              capacityName={detail.capacity_name}
            />
          ) : (
            <p className="py-8 text-center text-sm text-[var(--color-text-secondary)]">
              No workspace attribution data available
            </p>
          )}
        </Card>
      )}
    </PageLayout>
  );
};

export default CapacityDetailPage;
